// Workflow-Liste: Reiter nach Status, Suche, Besitzerfilter, Tabelle mit Auslöser, Läufen, Letzter Aktivität und Besitzer.
// „+ Neuer Workflow“ legt im Editor an. Gelöscht wird nie, nur archiviert.
import { useMemo, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { workflowsApi, type Workflow, type WorkflowStatus } from "../api/workflows";
import { useAuth } from "../auth/AuthContext";
import { Avatar, Laden } from "../components/Bausteine";
import { FehlerBox } from "../components/Fehler";
import { Icon } from "../components/Icon";
import { MenueKnopf, type MenuePunkt } from "../components/Menue";
import { useMeldung } from "../components/Meldungen";
import { Auswahl } from "../components/Auswahl";
import { useMeta } from "../daten/meta";
import { useSeitenKopf } from "../daten/seite";
import { relativ, zeitpunktLang } from "../werkzeuge/zeit";
import { useLaden } from "./daten";
import { ausloeserInfo } from "./hilfen";

type Reiter = "aktiv" | "entwurf" | "pausiert" | "archiviert";
const REITER: Reiter[] = ["aktiv", "entwurf", "pausiert", "archiviert"];
const REITER_LABEL: Record<Reiter, string> = { aktiv: "Aktiv", entwurf: "Entwürfe", pausiert: "Pausiert", archiviert: "Archiviert" };
const LEER_TEXT: Record<Reiter, string> = {
  aktiv: "Keine aktiven Workflows. Ein Workflow ist eine Folge von Schritten, die für Leads automatisch oder per Blitz-Knopf läuft.",
  entwurf: "Keine Entwürfe.",
  pausiert: "Keine pausierten Workflows.",
  archiviert: "Keine archivierten Workflows.",
};

export function ausloeserText(w: Workflow, statusLabel: (id: string) => string | undefined, typName: (id: string) => string | undefined): string {
  const a = w.ausloeser;
  const info = ausloeserInfo(a?.art ?? "manuell").label;
  if (a?.art === "statuswechsel") {
    const nach = ((a.config as { nach?: string[] }).nach ?? []).map((id) => statusLabel(id) ?? "?");
    const von = ((a.config as { von?: string[] }).von ?? []).map((id) => statusLabel(id) ?? "?");
    const teile = [von.length ? `von ${von.join(", ")}` : "", nach.length ? `nach ${nach.join(", ")}` : ""].filter(Boolean);
    return teile.length ? `${info}: ${teile.join(" ")}` : info;
  }
  if (a?.art === "custom_activity") {
    const id = (a.config as { type_id?: string }).type_id;
    return id ? `${info}: ${typName(id) ?? "?"}` : info;
  }
  return info;
}

export default function WorkflowListe() {
  useSeitenKopf([{ label: "Workflows" }]);
  const navigate = useNavigate();
  const meta = useMeta();
  const { nutzer } = useAuth();
  const meldung = useMeldung();
  const [params, setParams] = useSearchParams();
  const reiter = (REITER.includes(params.get("reiter") as Reiter) ? params.get("reiter") : "aktiv") as Reiter;
  const [suche, setSuche] = useState("");
  const [besitzer, setBesitzer] = useState<string | null>(null);
  const kannVerwalten = !!nutzer?.permissions.includes("workflows_verwalten");

  const offene = useLaden(() => workflowsApi.liste({ limit: 200 }), [], 20000);
  const archiv = useLaden(() => (reiter === "archiviert" ? workflowsApi.liste({ status: "archiviert", limit: 200 }) : Promise.resolve(null)), [reiter], 20000);

  const alle: Workflow[] = reiter === "archiviert" ? (archiv.daten?.data ?? []) : (offene.daten?.data ?? []);
  const zaehler = useMemo(() => {
    const z: Record<string, number> = { aktiv: 0, entwurf: 0, pausiert: 0 };
    for (const w of offene.daten?.data ?? []) z[w.status] = (z[w.status] ?? 0) + 1;
    return z;
  }, [offene.daten]);

  const n = suche.trim().toLocaleLowerCase("de");
  const zeilen = alle
    .filter((w) => w.status === reiter)
    .filter((w) => !besitzer || w.owner_id === besitzer)
    .filter((w) => !n || w.name.toLocaleLowerCase("de").includes(n) || (w.beschreibung ?? "").toLocaleLowerCase("de").includes(n));

  const besitzerOptionen = useMemo(() => {
    const ids = [...new Set([...(offene.daten?.data ?? []), ...(archiv.daten?.data ?? [])].map((w) => w.owner_id).filter(Boolean) as string[])];
    return ids.map((id) => ({ wert: id, label: meta.nutzerName(id) }));
  }, [offene.daten, archiv.daten, meta]);

  const neuLaden = async () => {
    await Promise.all([offene.neuLaden(), archiv.neuLaden()]);
  };
  const status = async (w: Workflow, s: WorkflowStatus) => {
    try {
      await workflowsApi.statusSetzen(w.id, s);
      meldung.ok(s === "aktiv" ? `„${w.name}“ ist aktiv.` : s === "pausiert" ? `„${w.name}“ pausiert.` : s === "archiviert" ? `„${w.name}“ archiviert.` : `„${w.name}“ ist wieder ein Entwurf.`);
      await neuLaden();
    } catch (e) {
      meldung.fehler(e, "Status nicht geändert");
    }
  };
  const duplizieren = async (w: Workflow) => {
    try {
      const r = await workflowsApi.duplizieren(w.id);
      navigate(`/workflows/${encodeURIComponent(r.workflow.id)}`);
    } catch (e) {
      meldung.fehler(e, "Nicht dupliziert");
    }
  };

  const menue = (w: Workflow): MenuePunkt[] => [
    { label: "Öffnen", icon: "pfeil-rechts", onClick: () => navigate(`/workflows/${encodeURIComponent(w.id)}`) },
    { label: "Läufe ansehen", icon: "aktivitaet", onClick: () => navigate(`/workflows/laeufe?workflow_id=${encodeURIComponent(w.id)}`) },
    { label: "Bericht ansehen", icon: "report", onClick: () => navigate(`/workflows/${encodeURIComponent(w.id)}/bericht`) },
    { label: "Duplizieren", icon: "kopieren", deaktiviert: !kannVerwalten, onClick: () => void duplizieren(w) },
    ...(kannVerwalten
      ? [
          ...(w.status === "aktiv" ? [{ label: "Pausieren", icon: "uhr" as const, trenner: true, onClick: () => void status(w, "pausiert") }] : []),
          ...(w.status === "pausiert" || w.status === "entwurf" ? [{ label: "Aktivieren", icon: "haken" as const, trenner: true, deaktiviert: (w.hinweise?.length ?? 0) > 0, hinweis: w.hinweise?.[0], onClick: () => void status(w, "aktiv") }] : []),
          ...(w.status !== "archiviert" ? [{ label: "Archivieren", icon: "schloss" as const, onClick: () => void status(w, "archiviert") }] : [{ label: "Aus dem Archiv holen", icon: "neu-laden" as const, trenner: true, onClick: () => void status(w, "entwurf") }]),
        ]
      : []),
  ];

  const gesamtLeer = offene.daten && offene.daten.data.length === 0 && reiter !== "archiviert";

  return (
    <div className="liste-seite wf-liste">
      <div className="liste-kopf">
        <h1 className="liste-kopf__titel">Workflows</h1>
        <div className="liste-kopf__rechts">
          {kannVerwalten ? (
            <Link to="/workflows/neu" className="knopf knopf--primaer">
              <Icon name="plus" groesse={14} />
              Neuer Workflow
            </Link>
          ) : null}
        </div>
      </div>
      <div className="liste-leiste wf-liste__leiste">
        <label className="suchfeld">
          <Icon name="suche" groesse={14} />
          <input value={suche} placeholder="Workflows suchen …" aria-label="Workflows suchen" onChange={(e) => setSuche(e.target.value)} />
          {suche ? (
            <button type="button" className="symbolknopf symbolknopf--klein" aria-label="Suche leeren" onClick={() => setSuche("")}>
              <Icon name="schliessen" groesse={12} />
            </button>
          ) : null}
        </label>
        <nav className="reiter" aria-label="Status">
          {REITER.map((r) => (
            <button key={r} type="button" className={`reiter__knopf${reiter === r ? " reiter__knopf--an" : ""}`} onClick={() => setParams(r === "aktiv" ? {} : { reiter: r }, { replace: true })}>
              {REITER_LABEL[r]}
              {r !== "archiviert" && zaehler[r] ? <span className="reiter__anzahl leise">{zaehler[r]}</span> : null}
            </button>
          ))}
          <span className="wf-liste__trenner" aria-hidden="true" />
          <Link to="/workflows/laeufe" className="reiter__knopf">
            Läufe
          </Link>
          <Link to="/workflows/berichte" className="reiter__knopf">
            Berichte
          </Link>
          <Link to="/workflows/blackout" className="reiter__knopf">
            Blackout Dates
          </Link>
        </nav>
        <span className="liste-leiste__rechts wf-liste__filter">
          <Auswahl optionen={besitzerOptionen} wert={besitzer} leerErlaubt platzhalter="Alle Nutzer" knopfKlasse="knopf knopf--klein" breite={220} onWahl={(w) => setBesitzer((w as string | null) ?? null)} />
        </span>
      </div>

      {offene.fehler && !offene.daten ? <FehlerBox fehler={offene.fehler} onNochmal={() => void neuLaden()} /> : null}
      {offene.laedt && !offene.daten ? (
        <Laden />
      ) : gesamtLeer ? (
        <div className="wf-leer-seite">
          <div className="wf-leer-kreis" aria-hidden="true">
            <Icon name="blitz" groesse={34} />
          </div>
          <h2>Workflows</h2>
          <p className="leise">Lege eine Folge von Schritten an, die für Leads automatisch läuft: Aufgaben, Anrufaufgaben, Zuweisen, Mails und SMS als Entwurf zur Prüfung.</p>
          {kannVerwalten ? (
            <Link to="/workflows/neu" className="knopf knopf--primaer">
              <Icon name="plus" groesse={14} />
              Neuer Workflow
            </Link>
          ) : null}
        </div>
      ) : !zeilen.length ? (
        <div className="leer">
          <div className="leer__titel">{n || besitzer ? "Keine Treffer." : REITER_LABEL[reiter]}</div>
          <div className="leer__text">{n || besitzer ? "Suche oder Filter ändern." : LEER_TEXT[reiter]}</div>
        </div>
      ) : (
        <div className="tabelle-rahmen">
          <table className="tabelle wf-liste__tabelle">
            <thead>
              <tr>
                <th>Name</th>
                <th>Auslöser</th>
                <th className="wf-zahl">Schritte</th>
                <th>Läufe</th>
                <th>Letzte Aktivität</th>
                <th>Besitzer</th>
                <th aria-label="Menü" />
              </tr>
            </thead>
            <tbody>
              {zeilen.map((w) => {
                const k = w.kennzahlen;
                return (
                  <tr key={w.id} onClick={() => navigate(`/workflows/${encodeURIComponent(w.id)}`)}>
                    <td className="wf-zelle-name">
                      <Link to={`/workflows/${encodeURIComponent(w.id)}`} onClick={(e) => e.stopPropagation()} className="wf-liste__name">
                        <Icon name="blitz" groesse={14} />
                        <span className="gekuerzt">{w.name}</span>
                      </Link>
                      {w.status !== reiter ? null : w.hinweise?.length && w.status !== "aktiv" ? (
                        <span className="wf-liste__unvollstaendig" title={w.hinweise.join(" ")}>
                          <Icon name="warnung" groesse={12} /> unvollständig
                        </span>
                      ) : null}
                    </td>
                    <td className="leise">{ausloeserText(w, (id) => meta.status(id)?.label, (id) => meta.typ(id)?.name)}</td>
                    <td className="wf-zahl">{w.schritte.length}</td>
                    <td className="wf-laufzahlen">
                      {k ? (
                        <>
                          <span title="Aktive Läufe (wartet oder läuft)">{k.aktiv} aktiv</span>
                          <span className="leise" title="Abgeschlossen"> · {k.fertig} fertig</span>
                          {k.fehler ? <span className="text-gefahr" title="Läufe mit Fehler"> · {k.fehler} Fehler</span> : null}
                        </>
                      ) : null}
                    </td>
                    <td className="leise" title={zeitpunktLang(w.letzter_lauf_at)}>
                      {w.letzter_lauf_at ? relativ(w.letzter_lauf_at) : "noch nie"}
                    </td>
                    <td>
                      {w.owner_id ? (
                        <span className="wf-besitzer">
                          <Avatar name={w.owner_name ?? meta.nutzerName(w.owner_id)} id={w.owner_id} groesse={20} />
                          <span className="gekuerzt">{w.owner_name ?? meta.nutzerName(w.owner_id)}</span>
                        </span>
                      ) : null}
                    </td>
                    <td className="wf-zelle-menue" onClick={(e) => e.stopPropagation()}>
                      <MenueKnopf knopfKlasse="symbolknopf" titel="Menü" punkte={menue(w)} ausrichtung="end" breite={220}>
                        <Icon name="mehr" groesse={16} />
                      </MenueKnopf>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
